import {
  AUDIO_FILE_TYPES,
  EMAIL_FILE_TYPES,
  IMAGE_FILE_TYPES,
  MS_FILE_TYPES,
  PDF_FILE_TYPES,
  VIDEO_FILE_TYPES,
  mimeToExtension,
} from "../../utils/constants";
import { fetchPresignedUrl } from "../../service/matter";

export const getDocumentStreamingUrl = (documentId) => {
  if (!documentId) {
    throw new Error('Document ID is required');
  }
  
  // Extract numeric ID from unique_id if it starts with 'D'
  let docId = documentId;
  if (typeof documentId === 'string' && documentId.startsWith('D')) {
    docId = documentId.substring(1);
  }
  
  const currentDate =  Date.now()
  return `${process.env.REACT_APP_STREAMING_URL}${docId}/?v=${currentDate}`;
};

/**
 * The backend returns Django-relative paths (e.g. "/api/documents/stream/…").
 * WebViewer fetches them itself, so a relative path would resolve against the
 * FRONTEND origin (stgdms), not the API origin (api.stgdms) the app's axios
 * clients use — the frontend host answers 400 for those paths. Absolutize
 * against REACT_APP_API_URL's origin before handing anything to the viewer.
 */
export const toApiAbsoluteUrl = (path) => {
  if (!path || /^https?:\/\//i.test(path)) {
    return path;
  }
  try {
    const apiOrigin = new URL(process.env.REACT_APP_API_URL).origin;
    return `${apiOrigin}${path.startsWith('/') ? '' : '/'}${path}`;
  } catch {
    return path;
  }
};

/**
 * Authenticated Django stream URL on the API origin — the same channel the
 * presign endpoint's `fallback_stream_url` uses. This is NOT the standalone
 * streaming service behind REACT_APP_STREAMING_URL, which serves the PDF
 * fast path but 500s on sparse/Office documents.
 */
export const getApiStreamFallbackUrl = (documentId) => {
  let docId = documentId;
  if (typeof documentId === 'string' && documentId.startsWith('D')) {
    docId = documentId.substring(1);
  }
  return toApiAbsoluteUrl(`/api/documents/stream/${docId}/`);
};

/**
 * Server-rendered email preview. EML/MSG source bytes are never handed to
 * WebViewer: the API converts the original record to an inline PDF first.
 */
export const getEmailPreviewUrl = (documentId) => {
  let docId = documentId;
  if (typeof documentId === 'string' && documentId.startsWith('D')) {
    docId = documentId.substring(1);
  }
  return toApiAbsoluteUrl(`/api/documents/email-preview/${docId}/`);
};

/**
 * Fetch a presigned S3 URL for direct browser-to-S3 document loading.
 * Falls back to the proxy streaming URL when the backend returns null
 * (non-S3 storage) or on any network error.
 */
const PREPARE_POLL_MS = 2000;
const PREPARE_MAX_MS = 10 * 60 * 1000;
// A giant record is converted before the viewer may have it, and the backend
// sends back its own estimate of how long that takes. Honour that estimate
// (with headroom) rather than giving up at a flat ten minutes, which is less
// than a 2.8GB record needs.
const PREPARE_ETA_HEADROOM = 3;

export const PREPARING_MESSAGE = 'Document is still being prepared for fast opening';
// A record whose stored file is incomplete will never finish preparing, so
// waiting is the wrong answer: say so and stop. Nine mirrored records on
// production stop mid-page with no end-of-file marker.
export const DAMAGED_MESSAGE =
  'This file is incomplete in storage, so it cannot be opened. '
  + 'It needs to be uploaded again.';
export const NEEDS_ATTENTION_MESSAGE =
  'This document could not be prepared for viewing. Support has been notified.';

/**
 * True when the backend declined to serve this document and said why, as
 * opposed to a transport failure worth retrying. Callers must show the
 * message rather than fall back to streaming the raw original.
 */
export const isDeliberateRefusal = (error) => (
  error?.message === PREPARING_MESSAGE
  || error?.message === DAMAGED_MESSAGE
  || error?.message === NEEDS_ATTENTION_MESSAGE
);

const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const metadataFromPresign = (data) => {
  const metadata = {};
  if (data?.file_name) metadata.fileName = data.file_name;
  if (data?.content_type) metadata.contentType = data.content_type;
  return metadata;
};

export const getPresignedDocumentUrl = async (documentId, options = {}) => {
  if (!documentId) {
    throw new Error('Document ID is required');
  }

  let docId = documentId;
  if (typeof documentId === 'string' && documentId.startsWith('D')) {
    docId = documentId.substring(1);
  }

  const pollMs = options.pollMs ?? PREPARE_POLL_MS;
  let maxWaitMs = options.maxWaitMs ?? PREPARE_MAX_MS;
  const started = Date.now();
  try {
    while (true) {
      const data = options.download
        ? await fetchPresignedUrl(docId, { download: true })
        : await fetchPresignedUrl(docId);
      const metadata = metadataFromPresign(data);
      if (data?.url) {
        return { ...metadata, url: data.url, isDirect: true };
      }

      // A document the backend refuses to hand over will never become
      // available by asking again. Fail immediately with the reason, so the
      // reader is told the truth instead of watching a spinner run down a
      // ten-minute clock.
      if (data?.preparation_state === 'damaged') {
        throw new Error(DAMAGED_MESSAGE);
      }
      if (data?.preparation_state === 'needs_attention') {
        throw new Error(NEEDS_ATTENTION_MESSAGE);
      }

      // Giant unconverted PDFs: wait for the fast-web copy instead of
      // handing the raw original to the viewer (that path OOMs).
      if (data?.preparing) {
        if (options.maxWaitMs == null && data?.eta_seconds) {
          maxWaitMs = Math.max(
            maxWaitMs, data.eta_seconds * PREPARE_ETA_HEADROOM * 1000,
          );
        }
        if (typeof options.onProgress === 'function') {
          options.onProgress({
            etaSeconds: data.eta_seconds ?? null,
            waitedMs: Date.now() - started,
            fileSize: data.file_size ?? null,
          });
        }
        if (Date.now() - started > maxWaitMs) {
          throw new Error(PREPARING_MESSAGE);
        }
        await sleep(pollMs);
        continue;
      }
      if (data?.fallback_stream_url) {
        // The Django stream endpoint authenticates via cookies; WebViewer's
        // own fetch is cross-origin from the frontend host, so it must opt in.
        return {
          ...metadata,
          url: toApiAbsoluteUrl(data.fallback_stream_url),
          isDirect: false,
          withCredentials: true,
        };
      }
      break;
    }
  } catch (err) {
    // These three are deliberate refusals, not transport failures. Falling
    // back to the streaming URL would hand the viewer the raw multi-gigabyte
    // original — the load that crashes the tab and is the reason the backend
    // withheld it in the first place.
    if (isDeliberateRefusal(err)) {
      throw err;
    }
    console.warn('Presigned URL fetch failed, using streaming fallback:', err?.message);
  }

  return {
    url: getDocumentStreamingUrl(documentId),
    isDirect: false,
  };
};

const resolveDownloadDocumentId = (doc) => {
  if (!doc) return null;
  const raw = doc.unique_id || doc.id || doc.document_id;
  if (raw == null || raw === "") return null;
  return String(raw);
};

/**
 * Save the original file to the user's computer.
 *
 * Direct S3/CloudFront links already carry a "save as <real filename>" header
 * (the backend sets it when download is requested), so they are handed straight
 * to the browser. The browser then shows its own download bar with live
 * progress at once, and file size no longer matters. Pulling a 500 MB record
 * into memory first meant minutes of silence with no progress bar.
 *
 * The cookie-authenticated stream fallback cannot be handed over this way (the
 * request must carry credentials), so that path still downloads via a blob.
 */
export const downloadDocumentFile = async (doc) => {
  const documentId = resolveDownloadDocumentId(doc);
  if (!documentId) {
    throw new Error("Document ID is required");
  }

  const presign = await getPresignedDocumentUrl(documentId, { download: true });
  if (!presign?.url) {
    throw new Error("No download URL returned");
  }

  const link = document.createElement("a");
  link.style.display = "none";

  if (presign.isDirect && !presign.withCredentials) {
    link.href = presign.url;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    return;
  }

  const response = await fetch(presign.url, {
    credentials: presign.withCredentials ? "include" : "omit",
  });
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  link.href = objectUrl;
  link.download = doc?.name || presign.fileName || "document";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

export const errorHandler = (message, setErrorMsg, setShowNotification) => {
  const id = Date.now().toString();
  const newMsg = message;
  setErrorMsg([{ id, newMsg }]);
  setShowNotification(true);
};

export const setupBreadcrumbTrail = (
  rename,
  dispatch,
  fetchBreadCrumbs,
  urlIds,
  setDocumentBreadcrumb
) => {
  if (rename) {
    dispatch(fetchBreadCrumbs({ item_id: urlIds.at(-1) })).then((response) => {
      setDocumentBreadcrumb([
        { id: 0, name: "Matters" },
        ...(response?.payload?.data || []),
      ]);
    });
  }
};

const normalizeFileValue = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .split(";", 1)[0]
    .trim();

const extensionFromValue = (value) => {
  if (!value) return "";
  const normalizedValue = normalizeFileValue(value);
  if (mimeToExtension[normalizedValue]) {
    return mimeToExtension[normalizedValue];
  }

  const withoutQuery = normalizedValue.split(/[?#]/, 1)[0];
  const lastPathPart = withoutQuery.split("/").pop() || "";
  const extension = lastPathPart.includes(".")
    ? lastPathPart.split(".").pop()
    : lastPathPart;
  return extension.replace(/[^a-z0-9]+$/g, "");
};

export const getFileExtension = (value) => extensionFromValue(value);

const matchesFileType = (value, types) => {
  const normalizedValue = normalizeFileValue(value);
  const extension = extensionFromValue(normalizedValue);
  return types.some((type) => {
    const normalizedType = type.toLowerCase();
    return normalizedValue === normalizedType || extension === normalizedType;
  });
};

export const getFileType = (fileType) => {
  if (!fileType) return "unknown";

  if (matchesFileType(fileType, PDF_FILE_TYPES)) return "pdf";
  if (matchesFileType(fileType, EMAIL_FILE_TYPES)) return "email";
  if (matchesFileType(fileType, MS_FILE_TYPES)) return "office";
  if (matchesFileType(fileType, IMAGE_FILE_TYPES)) return "image";
  if (matchesFileType(fileType, AUDIO_FILE_TYPES)) return "audio";
  if (matchesFileType(fileType, VIDEO_FILE_TYPES)) return "video";

  return "unknown";
};

/**
 * Return the extension Apryse should use when loading a document.
 *
 * DMS has legacy PDF rows whose names are not suffixed with ".pdf", so
 * unknown files retain the old PDF fallback. Real images and Office files
 * must use their actual extension; otherwise Apryse attempts to parse them
 * as PDFs.
 */
export const getApryseExtension = (fileNameOrType) => {
  const fileType = getFileType(fileNameOrType);
  if (fileType === "image" || fileType === "office") {
    return getFileExtension(fileNameOrType) || "pdf";
  }
  return "pdf";
};

const MEDIA_MIME_TYPES = {
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  aac: "audio/aac",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  flac: "audio/flac",
  mp4: "video/mp4",
  webm: "video/webm",
  ogv: "video/ogg",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  mpeg: "video/mpeg",
  mpg: "video/mpeg",
};

export const getMediaMimeType = (fileNameOrType) => {
  const normalizedValue = normalizeFileValue(fileNameOrType);
  if (normalizedValue.includes("/")) return normalizedValue;
  return MEDIA_MIME_TYPES[getFileExtension(normalizedValue)] || "";
};

const GENERIC_MIME_TYPES = new Set([
  "",
  "application/octet-stream",
  "binary/octet-stream",
]);

/**
 * Resolve the renderer-facing metadata once. A generic server MIME type must
 * not hide a useful filename extension returned alongside it.
 */
export const getPreviewFileInfo = ({
  fileName = "",
  contentType = "",
} = {}) => {
  const normalizedContentType = normalizeFileValue(contentType);
  const usableContentType = GENERIC_MIME_TYPES.has(normalizedContentType)
    ? ""
    : normalizedContentType;
  const contentFileType = getFileType(usableContentType);
  const fileType = contentFileType === "unknown"
    ? getFileType(fileName)
    : contentFileType;
  const typeValue = usableContentType || fileName;

  return {
    fileType,
    extension: getApryseExtension(typeValue),
    mimeType: usableContentType || (
      fileType === "audio" || fileType === "video"
        ? getMediaMimeType(fileName)
        : ""
    ),
  };
};

// Lazy import to keep DocumentUtils free of a static TabView dependency
// (several standalone viewers import this module).
const suppressStockBookmarksPanel = (instance) => {
  try {
    // eslint-disable-next-line global-require
    const orderedBookmarks = require("../TabView/orderedBookmarks");
    orderedBookmarks.suppressStockBookmarksPanel(instance);
  } catch (_) {}
};

export const applyApryseFeatureRestrictions = (instance) => {
  if (!instance?.UI) return;
  const { UI, Core } = instance;
  try {
    UI.disableElements?.([
      'toolbarGroup-Measure',
      'toolbarGroup-Redact',
      'toolbarGroup-EditText',
      'toolbarGroup-Forms',
      'toolbarGroup-FillAndSign',

      // NOTE: the Outlines panel ('outlinesPanel' et al.) is intentionally
      // NOT disabled — it is where bookmarks embedded in PDFs by other
      // applications (Acrobat, Word exports) appear. Apryse's "Bookmarks"
      // panel only shows bookmarks created inside WebViewer.

      // Rotation icon
      'thumbRotateClockwise',
      
      // 'thumbRotateCounterclockwise',
      'thumbRotateCounterClockwise',

      // Page orientation
      'rotatePageClockwise',
      'rotatePageCounterClockwise',
      'rotateClockwiseButton',
      'rotateCounterClockwiseButton',

      // Replace page
      'replacePage'
    ]);
  } catch (e) {
    console.warn('Failed to disable Apryse UI elements:', e);
  }

  // configurePermissions CASE 3 runs UI.enableAllElements(), which clears
  // the disabled state and resurrects the stock bookmarks panel that the
  // ordered-bookmarks panel replaced (devdms repro, 2026-07-22). Every
  // permission pass ends here, so re-assert the hide (no-op on instances
  // without the ordered panel).
  suppressStockBookmarksPanel(instance);

  try {
    const ToolNames = Core?.Tools?.ToolNames;
    if (!ToolNames || typeof UI.disableTools !== 'function') return;

    UI.disableTools([
      // Measure
      ToolNames.CALIBRATION_MEASUREMENT,
      ToolNames.DISTANCE_MEASUREMENT,
      ToolNames.PERIMETER_MEASUREMENT,
      ToolNames.AREA_MEASUREMENT,
      ToolNames.COUNT_MEASUREMENT,
      ToolNames.ARC_MEASUREMENT,
      ToolNames.ELLIPSE_MEASUREMENT,
      ToolNames.RECTANGULAR_AREA_MEASUREMENT,
      ToolNames.CLOUDY_RECTANGULAR_AREA_MEASUREMENT,

      // Redact
      ToolNames.REDACTION,
    ].filter(Boolean));
  } catch (e) {
    console.warn('Failed to apply Apryse feature restrictions:', e);
  }
};

export const configurePermissions = (
  instance,
  isLocked,
  type,
  currentUserId,
  lockedByUserId
) => {
  if (!instance) {
    return {
      success: false,
      mode: 'error',
      message: 'WebViewer instance not available'
    };
  }

  const { UI, Core } = instance;
  
// isLockedByOtherUser needs strict equality check for lock status AND different users
  const isLockedByOtherUser = isLocked === true && lockedByUserId !== currentUserId;

  const fileType = type || "pdf";

  // CASE 1: Office files - Always view-only (regardless of lock status)
  if (fileType != 'pdf') {
    UI.setToolbarGroup('toolbarGroup-View');
    Core.annotationManager.enableReadOnlyMode();
    
    UI.disableElements([
      'annotationTools',
      'toolbarGroup-Annotate',
      'toolbarGroup-Edit',
      'toolbarGroup-Shapes',
      'toolbarGroup-Insert',
      'toolbarGroup-Forms',
      'toolbarGroup-FillAndSign',
      'ribbons',
      'editorDropdown',
      'toolsHeader',
    ]);

    applyApryseFeatureRestrictions(instance);
    
    return {
      success: true,
      mode: 'view-only',
      reason: 'office-file',
      message: 'Office file - view-only mode applied'
    };
  }

  // CASE 2: PDF locked by another user - View-only mode
  if (isLockedByOtherUser) {
    UI.setToolbarGroup('toolbarGroup-View');
    Core.annotationManager.enableReadOnlyMode();
    
    UI.disableElements([
      'annotationTools',
      'toolbarGroup-Annotate',
      'toolbarGroup-Edit',
      'toolbarGroup-Shapes',
      'toolbarGroup-Insert',
      'toolbarGroup-Forms',
      'toolbarGroup-FillAndSign'
    ]);

    applyApryseFeatureRestrictions(instance);
    
    return {
      success: true,
      mode: 'view-only',
      reason: 'locked-by-other-user',
      message: `Document locked by user ${lockedByUserId}`
    };
  }

  // CASE 3: PDF not locked OR locked by current user - Enable editing
  UI.enableAllElements();
  Core.annotationManager.disableReadOnlyMode();
  applyApryseFeatureRestrictions(instance);
  // Don't force switch to View mode when enabling editing - preserve user's current editing mode
  // This prevents switching to View mode while user is actively typing/editing
  // Only set to View on initial load (handled elsewhere), not when reconfiguring permissions
  
  return {
    success: true,
    mode: 'editable',
    reason: isLocked ? 'locked-by-current-user' : 'not-locked',
    message: 'Full editing enabled'
  };
};
